import React from 'react';
import {
  ArrowShapeUtil,
  CubicBezier2d,
  Group2d,
  getDefaultColorTheme,
  STROKE_SIZES,
  SVGContainer,
  useDefaultColorTheme,
  Vec,
} from 'tldraw';
import {
  readUnboundArrowTerminals,
  syncUnboundArrowTerminalsOverTargets,
} from '../utils/arrowBindOnTranslate';
import { getAlignedArrowLocalCurve } from '../utils/alignedArrowCurve';
import { arrowheadPath, cubicPath, shortenEnd } from '../utils/alignedConnector';

/** Remember which ends were free when translate started (WeakMap key = initial shape). */
const unboundTerminalsAtTranslateStart = new WeakMap();

function strokeWidthFor(shape) {
  const size = STROKE_SIZES[shape?.props?.size] || STROKE_SIZES.m;
  return size * (shape?.props?.scale || 1);
}

function dashArray(dash, width) {
  if (dash === 'dashed') return `${width * 3} ${width * 2}`;
  if (dash === 'dotted') return `${width} ${width * 2}`;
  return undefined;
}

function visibleCurve(shape, curve) {
  const width = strokeWidthFor(shape);
  const hasEndHead = shape?.props?.arrowheadEnd && shape.props.arrowheadEnd !== 'none';
  if (!hasEndHead) return { ...curve, tip: curve.end, head: null, width };
  const length = width * 3.2;
  const strokeEnd = shortenEnd(curve.end, curve.cp2, length);
  return {
    ...curve,
    end: strokeEnd,
    tip: curve.end,
    head: arrowheadPath(curve.end, curve.cp2, length, width * 2.4),
    width,
  };
}

function AlignedArrowBody({ shape, curve }) {
  const theme = useDefaultColorTheme();
  const color = theme?.[shape.props.color]?.solid || '#1d1d1d';
  return (
    <SVGContainer style={{ minWidth: 50, minHeight: 50 }}>
      <AlignedArrowPaths shape={shape} curve={curve} color={color} />
    </SVGContainer>
  );
}

function AlignedArrowPaths({ shape, curve, color }) {
  const drawn = visibleCurve(shape, curve);
  const dash = dashArray(shape?.props?.dash, drawn.width);
  return (
    <g
      fill="none"
      stroke={color}
      strokeWidth={drawn.width}
      strokeLinecap="round"
      strokeLinejoin="round"
      pointerEvents="none"
    >
      <path d={cubicPath(drawn.start, drawn.cp1, drawn.cp2, drawn.end)} strokeDasharray={dash} />
      {drawn.head ? <path d={drawn.head} fill={color} stroke="none" /> : null}
    </g>
  );
}

/**
 * Board connect lines: stock tldraw only binds when dragging handles.
 * Also bind free ends when the user moves (translates) the whole line onto pins/notes/frames.
 *
 * The curve itself is not user-shaped. It leaves the facing edge of one card
 * and enters the facing edge of the other.
 */
export class BoardArrowShapeUtil extends ArrowShapeUtil {
  getGeometry(shape) {
    const curve = getAlignedArrowLocalCurve(this.editor, shape);
    // tldraw's select tool reads geometry.children[1] as the label.
    // A bare bezier has no children and crashes the click.
    const body = new CubicBezier2d({
      start: new Vec(curve.start.x, curve.start.y),
      cp1: new Vec(curve.cp1.x, curve.cp1.y),
      cp2: new Vec(curve.cp2.x, curve.cp2.y),
      end: new Vec(curve.end.x, curve.end.y),
    });
    return new Group2d({ children: [body] });
  }

  getHandles(shape) {
    const curve = getAlignedArrowLocalCurve(this.editor, shape);
    return [
      {
        id: 'start',
        type: 'vertex',
        index: 'a1',
        x: curve.start.x,
        y: curve.start.y,
      },
      {
        id: 'end',
        type: 'vertex',
        index: 'a3',
        x: curve.end.x,
        y: curve.end.y,
      },
    ];
  }

  onHandleDrag(shape, info) {
    if (info?.handle?.id === 'middle') return undefined;
    return super.onHandleDrag(shape, info);
  }

  component(shape) {
    const curve = getAlignedArrowLocalCurve(this.editor, shape);
    return <AlignedArrowBody shape={shape} curve={curve} />;
  }

  toSvg(shape, ctx) {
    const theme = getDefaultColorTheme(ctx);
    const curve = getAlignedArrowLocalCurve(this.editor, shape);
    const color = theme?.[shape.props.color]?.solid || '#1d1d1d';
    const scale = shape.props.scale || 1;
    return (
      <g transform={`scale(${1 / scale})`}>
        <AlignedArrowPaths shape={shape} curve={curve} color={color} />
      </g>
    );
  }

  indicator(shape) {
    const curve = getAlignedArrowLocalCurve(this.editor, shape);
    const drawn = visibleCurve(shape, curve);
    return <path d={cubicPath(drawn.start, drawn.cp1, drawn.cp2, drawn.end)} />;
  }

  onTranslateStart(shape) {
    const result = super.onTranslateStart?.(shape);
    try {
      unboundTerminalsAtTranslateStart.set(shape, readUnboundArrowTerminals(this.editor, shape));
    } catch {
      /* ignore */
    }
    return result;
  }

  onTranslate(initialShape, shape) {
    const result = super.onTranslate?.(initialShape, shape);
    const terminals = unboundTerminalsAtTranslateStart.get(initialShape);
    if (terminals) {
      try {
        syncUnboundArrowTerminalsOverTargets(this.editor, shape.id, terminals);
      } catch {
        /* ignore */
      }
    }
    return result;
  }

  onTranslateEnd(initialShape, shape) {
    const result = super.onTranslateEnd?.(initialShape, shape);
    const terminals = unboundTerminalsAtTranslateStart.get(initialShape);
    unboundTerminalsAtTranslateStart.delete(initialShape);
    if (terminals) {
      try {
        syncUnboundArrowTerminalsOverTargets(this.editor, shape.id, terminals);
      } catch {
        /* ignore */
      }
    }
    return result;
  }

  onTranslateCancel(initialShape, shape) {
    unboundTerminalsAtTranslateStart.delete(initialShape);
    return super.onTranslateCancel?.(initialShape, shape);
  }
}
